import './signInPinVerifyPage.scss';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { red } from '@mui/material/colors';
import Snackbar from '@mui/material/Snackbar';
import { ThemeProvider } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import { AuthType } from '@thesavingsgroup/auth/AuthType';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import isNaN from 'lodash/isNaN';
import uniqueId from 'lodash/uniqueId';
import {
  ChangeEvent,
  ClipboardEvent,
  KeyboardEvent,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import heroReferenceMessage from '../../assets/hero-reference-message.svg';
import logo from '../../assets/logo-default.svg';
import { inputEventHandler } from '../../components/utils/inputEventHandler';
import useApiStore from '../../store/apiStore/useApiStore';
import useParameterStore from '../../store/parameterStore/useParameterStore';
import { DeliverPinLocation } from '../../store/pinStore/enums/DeliverPinLocation';
import { useCountdownTimer } from '../../store/pinStore/hooks/useCountdownTimer';
import usePinStore from '../../store/pinStore/usePinStore';
import useSignOutStore from '../../store/signOutStore/useSignOutStore';
import useThemeStore from '../../store/themeStore/useThemeStore';
import useUserStore from '../../store/userStore/useUserStore';
import { locator } from '../../testing/e2e-locator/locator';

export function Component(): ReactNode {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { t } = useTranslation();
  const { isLoading } = useApiStore();
  const {
    primaryPhoneNumber,
    deliverPin,
    deliverPinRetrySuccess,
    deliverPinFailedMessage,
  } = usePinStore();
  const {
    userAuthenticate,
    userAuthenticateFailedMessage,
    userAuthenticateLocked,
    userAuthenticateSuccess,
  } = useUserStore();
  const { signOut } = useSignOutStore();
  const { register, handleSubmit, reset, getValues, setValue } = useForm();
  const { leadChannelCode } = useParameterStore();

  const amountOfInputs = 6;
  const InputsList = [...Array(amountOfInputs).keys()];
  const inputRef = useRef<any>([]);
  const [focusedInputIndex, setFocusedInputIndex] = useState(0);

  const resendTimerDuration = 15; // in seconds
  const [seconds, setSecondsLeft] = useState(0);
  const { startTimer, stopTimer } = useCountdownTimer(
    setSecondsLeft,
    resendTimerDuration,
  );

  useEffect((): (() => void) => {
    if (!primaryPhoneNumber) {
      navigate('/sign-in');
    }

    if (userAuthenticateLocked) {
      signOut({
        withMessage: userAuthenticateFailedMessage,
      });
    }

    startTimer();

    return (): void => {
      stopTimer();
    };
  }, [primaryPhoneNumber, userAuthenticateLocked, userAuthenticateSuccess]);

  useEffect((): void => {
    if (userAuthenticateFailedMessage) {
      reset();
    }
  }, [userAuthenticateFailedMessage]);

  const buttonText = useMemo((): string => {
    if (seconds > 0) {
      return t('SignInPinVerifyPage.resendCodeDelay', {
        delay: seconds,
      });
    }
    return t('SignInPinVerifyPage.resendCode');
  }, [t, seconds]);

  const formSubmit = async (values: FieldValues): Promise<void> => {
    const pin = Object.values(values).join('');

    if (primaryPhoneNumber && pin) {
      await userAuthenticate(
        primaryPhoneNumber,
        pin,
        leadChannelCode,
        AuthType.ACCOUNT_SIGN_IN_USING_PIN,
      );
    }
  };

  const resendCode = async (): Promise<void> => {
    if (primaryPhoneNumber) {
      startTimer();

      useUserStore.setState({
        userAuthenticateFailedMessage: null,
        userAuthenticateLocked: false,
        userAuthenticateSuccess: false,
      });

      await deliverPin({
        deliverPinLocation: DeliverPinLocation.RETRY,
        phone: primaryPhoneNumber,
        purpose: AuthType.ACCOUNT_SIGN_IN_USING_PIN,
      });

      inputRef.current[0].focus();
    }
  };

  const focusCurrentInput = (): void => {
    const formValues = Object.values(getValues()).join('');
    const numOfFilledInputs = formValues.length;

    const currentInput = inputRef.current[numOfFilledInputs];
    if (currentInput) {
      currentInput.focus();
    }
  };

  const determineInputFocus =
    (index: number) =>
    (event: KeyboardEvent<HTMLInputElement>): void => {
      // Detect backspace to focus the previous input
      if (event.key === 'Backspace' || event.key === 'Delete') {
        const previousInput = inputRef.current[index - 1];

        if (previousInput) {
          setValue(`verificationCode-${index - 1}`, '');

          previousInput.focus();
        }
      }

      // Detect current input value was not provided.
      // Needed to prevent focusing of next input due to
      // holding down of multiple keys at once
      const currentInput = inputRef.current[index];
      if (!currentInput.value) {
        return;
      }

      // Detect number was entered to focus the next input
      if (!isNaN(parseInt(event.key, 10))) {
        const nextInput = inputRef.current[index + 1];

        if (nextInput) {
          nextInput.focus();
        } else {
          currentInput.blur();

          // Submit form when last input value was provided
          handleSubmit(formSubmit)();
        }
      }
    };

  const splitOneTimeCode = (event: ChangeEvent<HTMLInputElement>): void => {
    const value = get(event, 'target.value');

    if (isNaN(parseInt(value, 10)) || value.length !== amountOfInputs) {
      return;
    }

    const chars: string[] = value.split('');

    chars.forEach((char, index): void => {
      setValue(`verificationCode-${index}`, char);
    });

    const lastInput = inputRef.current[inputRef.current.length - 1];
    if (lastInput) {
      lastInput.focus();
    }

    handleSubmit(formSubmit)();
  };

  const capturePasteEvent = (event?: ClipboardEvent): void => {
    // get clipboard data from paste event
    const clipboardData = get(event, 'clipboardData');

    if (clipboardData) {
      const value = clipboardData.getData('Text');

      // prevent paste if non-digits were provided
      if (!/^\d+$/g.test(value)) {
        event.preventDefault();
        return;
      }

      let nextInput;

      const maxCharsProvided = value.length >= amountOfInputs;

      if (value.length > 1) {
        // handle multiple digits being pasted
        // convert value to chars array, sliced to the max amount of inputs
        const chars: string[] = value.split('').slice(0, amountOfInputs);

        // assign the char value to each input
        chars.forEach((char, index): void => {
          setValue(`verificationCode-${index}`, char);
        });

        // determine the next input index
        const lastCharInputIndex = maxCharsProvided
          ? amountOfInputs - 1 // next input should be the last if max characters
          : value.length;

        nextInput = inputRef.current[lastCharInputIndex];
      } else if (value.length === 1) {
        // single digit was pasted, set value the currently focused input
        setValue(`verificationCode-${focusedInputIndex}`, value);

        // determine the next input
        nextInput = inputRef.current[focusedInputIndex + 1];
      }

      // prevent paste event from carrying over to the next input when focused
      event.preventDefault();

      // added as a fail safe but the next input to focus should always exist
      if (nextInput) {
        nextInput.focus();
      }

      // all chars were provided, submit the form
      if (maxCharsProvided) {
        handleSubmit(formSubmit)();
      }
    }
  };

  return (
    <div className="sign-in-pin-verify-page">
      <ThemeProvider theme={theme}>
        <Box sx={theme.pageLayout}>
          {deliverPinRetrySuccess && (
            <Snackbar
              anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
              autoHideDuration={5000}
              open
              onClose={(): void => {
                usePinStore.setState({ deliverPinRetrySuccess: false });
              }}
            >
              <Alert
                data-testid={locator.pages.signInPinVerify.alert.success}
                icon={<CheckCircleIcon />}
                severity="success"
              >
                {t('SignInPinVerifyPage.codeSent')}
              </Alert>
            </Snackbar>
          )}

          {!isEmpty(deliverPinFailedMessage) && (
            <Snackbar
              anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
              autoHideDuration={5000}
              open
              onClose={(): void => {
                usePinStore.setState({ deliverPinFailedMessage: null });
              }}
            >
              <Alert
                data-testid={locator.pages.signInPinVerify.alert.error}
                severity="error"
              >
                {deliverPinFailedMessage}
              </Alert>
            </Snackbar>
          )}

          <header data-testid={locator.pages.signInPinVerify.header.root}>
            <img alt={t('imageAlts.logo')} src={logo} />
            <Link
              data-testid={locator.pages.signInPinVerify.header.signUpLink}
              to="/sign-up"
            >
              {t('SignInPinVerifyPage.signup')}
            </Link>
          </header>

          <main>
            <figure>
              <img
                alt={t('imageAlts.heroReference')}
                src={heroReferenceMessage}
              />
            </figure>
            <form
              data-testid={locator.pages.signInPinVerify.form.root}
              noValidate
              onSubmit={handleSubmit(formSubmit)}
            >
              <div className="form-content">
                <h1 data-testid={locator.pages.signInPinVerify.form.h1}>
                  {t('SignInPinVerifyPage.title')}
                </h1>
                <h3 data-testid={locator.pages.signInPinVerify.form.h3}>
                  <Trans
                    components={[
                      <span
                        key={uniqueId('span-')}
                        style={{ textDecoration: 'underline' }}
                      />,
                    ]}
                    i18nKey={'SignInPinVerifyPage.subtitle'}
                    values={{
                      phoneNumber:
                        inputEventHandler.phoneNumber.formatter(
                          primaryPhoneNumber,
                        ),
                    }}
                  />
                </h3>

                <div className="inputs-container" onClick={focusCurrentInput}>
                  {InputsList.map(
                    (index): ReactNode => (
                      <TextField
                        {...register(`verificationCode-${index}`)}
                        autoComplete={'one-time-code'}
                        autoFocus={index === 0}
                        disabled={isLoading}
                        error={!isEmpty(userAuthenticateFailedMessage)}
                        inputRef={(element: HTMLInputElement): void => {
                          inputRef.current[index] = element;
                        }}
                        key={index}
                        slotProps={{
                          htmlInput: {
                            'data-testid':
                              locator.pages.signInPinVerify.form[
                                `pinInput${index + 1}`
                              ],
                            inputMode: 'numeric',
                          },
                        }}
                        style={{
                          pointerEvents:
                            focusedInputIndex !== index ? 'none' : 'auto',
                        }}
                        onChange={splitOneTimeCode}
                        onFocus={(): void => {
                          setFocusedInputIndex(index);
                        }}
                        onKeyDown={inputEventHandler.verificationCode.keyDown}
                        onKeyUp={determineInputFocus(index)}
                        onPasteCapture={capturePasteEvent}
                      />
                    ),
                  )}
                </div>

                <div style={{ marginBottom: '3rem' }}>
                  {!isEmpty(userAuthenticateFailedMessage) && (
                    <span
                      data-testid={
                        locator.pages.signInPinVerify.form.pinInputError
                      }
                      style={{
                        fontSize: '12px',
                        color: red[700],
                      }}
                    >
                      {userAuthenticateFailedMessage}
                    </span>
                  )}
                </div>

                <Button
                  color="primary"
                  data-testid={locator.pages.signInPinVerify.form.resendPinLink}
                  disabled={seconds > 0}
                  type="button"
                  variant="contained"
                  fullWidth
                  onClick={resendCode}
                >
                  {buttonText}
                </Button>
              </div>
              <footer data-testid={locator.pages.signInPinVerify.footer.root}>
                {t('copyright', { year: new Date().getFullYear() })}
              </footer>
            </form>
          </main>
        </Box>
      </ThemeProvider>
    </div>
  );
}

Component.displayName = 'SignInPinVerifyPage';
